import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import type { TournamentView } from '@/models/tournament';
import { TournamentStatus, TournamentFormat, resolveEnum } from '@/models/tournament';

interface TournamentCardProps {
  tournament: TournamentView;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const TournamentCard: React.FC<TournamentCardProps> = ({ tournament, onView, onEdit, onDelete }) => {
  const { t, i18n } = useTranslation('tournament');

  // Normalize status: API may return string ("Upcoming") or numeric (0) due to StringEnumConverter
  const normalizeStatus = (status: TournamentStatus | string | number): TournamentStatus => {
    if (typeof status === 'string') {
      const key = status as keyof typeof TournamentStatus;
      return key in TournamentStatus ? TournamentStatus[key] : TournamentStatus.Upcoming;
    }
    return status as TournamentStatus;
  };

  const normalizedStatus = normalizeStatus(tournament.status);

  const statusKeys: Record<TournamentStatus, string> = {
    0: 'Upcoming',
    1: 'Active',
    2: 'Completed',
    3: 'Cancelled',
  };

  const getStatusColor = (status: TournamentStatus): string => {
    switch (status) {
      case TournamentStatus.Active:
        return 'bg-green-100 text-green-800';
      case TournamentStatus.Upcoming:
        return 'bg-blue-100 text-blue-800';
      case TournamentStatus.Completed:
        return 'bg-gray-100 text-gray-800';
      case TournamentStatus.Cancelled:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const normalizedFormat = resolveEnum(TournamentFormat, tournament.format) ?? TournamentFormat.League;

  const formatKeys: Record<TournamentFormat, string> = {
    [TournamentFormat.League]: 'League',
    [TournamentFormat.Knockout]: 'Knockout',
    [TournamentFormat.GroupStage]: 'Group Stage',
  };

  const getFormatStyle = (format: TournamentFormat): { color: string; icon: string } => {
    switch (format) {
      case TournamentFormat.Knockout:
        return { color: 'bg-amber-100 text-amber-800', icon: 'GitBranch' };
      case TournamentFormat.GroupStage:
        return { color: 'bg-purple-100 text-purple-800', icon: 'Grid3X3' };
      default:
        return { color: 'bg-indigo-100 text-indigo-800', icon: 'LayoutList' };
    }
  };

  const formatStyle = getFormatStyle(normalizedFormat);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-lg hover:border-primary/30 transition-all duration-200 group" data-testid={`tournament-card-${tournament.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors duration-200">
            {tournament.name}
          </h3>
          <p className="text-xs text-gray-500">{t('ID:')} {tournament.id}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${formatStyle.color}`}>
            <Icon name={formatStyle.icon} size={12} />
            {t(formatKeys[normalizedFormat])}
          </span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(normalizedStatus)}`}>
            {t(statusKeys[normalizedStatus] ?? 'Unknown')}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {tournament.description}
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-border">
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Calendar" size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('Matches')}</p>
            <p className="text-lg font-semibold text-gray-900">{tournament.matchCount}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Icon name="Users" size={18} className="text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{t('Teams')}</p>
            <p className="text-lg font-semibold text-gray-900">{tournament.participants}</p>
          </div>
        </div>
      </div>

      {/* Date Range */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-600 mb-1">
          <Icon name="CalendarDays" size={14} className="me-2 text-gray-400" />
          <span className="font-medium">{t('Date Range')}</span>
        </div>
        <p className="text-sm text-gray-900 ms-6">
          {formatDate(tournament.startDate)} - {formatDate(tournament.endDate)}
        </p>
      </div>

      {/* Venue */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Icon name="MapPin" size={14} className="me-2 text-gray-400" />
          <span>{tournament.venue}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-3 gap-2">
        <Button
          onClick={onView}
          variant="default"
          size="sm"
          className="w-full"
          data-testid={`tournament-view-btn-${tournament.id}`}
        >
          <Icon name="Eye" size={14} className="me-1" />
          {t('View')}
        </Button>
        <Button
          onClick={onEdit}
          variant="outline"
          size="sm"
          className="w-full"
          data-testid={`tournament-edit-btn-${tournament.id}`}
        >
          <Icon name="Edit" size={14} className="me-1" />
          {t('Edit')}
        </Button>
        <Button
          onClick={onDelete}
          variant="outline"
          size="sm"
          className="w-full text-red-600 hover:bg-red-50 hover:border-red-300 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:border-red-700"
          data-testid={`tournament-delete-btn-${tournament.id}`}
        >
          <Icon name="Trash2" size={14} className="me-1" />
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default TournamentCard;
